import { useState, useEffect } from "react"
import MediaCaptureField from '../../common/MediaCaptureField.js'
import { onContractPaymentService } from "../../../services/onContractPaymentService"
import SelectField from '../../common/SelectField.js';
import { useAuth } from "../../../context/AuthContext"
import Alert from "../../common/Alert"
import { validators, inputFilters, useValidation, ValidationMessage } from '../../common/Validation.js'
import { X, IndianRupee } from "lucide-react"
import Button from '../../common/Button.js'
import { useTranslation } from 'react-i18next';


const PAYMENT_MODES = ["Cash", "UPI"]

const EditPaymentModal = ({ contractId, payment, balanceAmount = 0, onClose, onSuccess }) => {
  const { user } = useAuth()
  const [saving, setSaving] = useState(false)
  const [form, setForm] = useState({
    amount: "",
    payment_date: "",
    payment_mode: "",
    reference_number: "",
    remarks: "",
  })
  const { errors, validate, validateField } = useValidation()
  const [receipt, setReceipt] = useState(null)

  const validationRules = {
    amount:       (v) => validators.required(v, 'Amount') || validators.number(v, { min: 0.01, label: 'Amount' }),
    payment_date: (v) => validators.required(v, 'Payment date'),
    payment_mode: (v) => validators.select(v, 'payment mode'),
  }

  useEffect(() => {
    if (payment) {
      setForm({
        amount: payment.amount ?? "",
        payment_date: payment.payment_date ?? "",
        payment_mode: payment.payment_mode ?? "",
        reference_number: payment.reference_number ?? "",
        remarks: payment.remarks ?? "",
      })
    }
  }, [payment])

  const handleChange = (e) => {
    const { name, value } = e.target

    let filtered = value
    if (name === 'amount') filtered = inputFilters.amount(value)

    setForm({ ...form, [name]: filtered })

    if (validationRules[name]) {
      validateField(name, filtered, validationRules[name])
    }
  }

  const handleSubmit = async () => {
    if (!validate(form, validationRules)) return
    const maxAllowed = balanceAmount + parseFloat(payment?.amount || 0)
    if (parseFloat(form.amount) > maxAllowed) {
      Alert.error(`Amount cannot exceed ₹${maxAllowed.toFixed(2)}`)
      return
    }
    try {
      setSaving(true)
      const payload = new FormData()
      Object.entries({ ...form, amount: parseFloat(form.amount), updated_by: user?.user_id }).forEach(([k, v]) => {
        if (v !== "" && v !== null && v !== undefined) payload.append(k, v)
      })
      if (receipt) payload.append("receipt", receipt)
      await onContractPaymentService.update(contractId, payment.payment_id, payload)
      Alert.success("Payment updated successfully")
      onSuccess()
      onClose()
    } catch (err) {
      Alert.error(err.response?.data?.message || "Failed to update payment")
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-3">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <IndianRupee className="w-4 h-4 text-orange-500" />
            <h2 className="font-bold text-secondary text-base">Edit Payment — {payment?.payment_id}</h2>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="px-5 py-4 space-y-4">
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">
              Amount (₹) <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="amount"
              value={form.amount}
              onChange={handleChange}
              placeholder="0.00"
              className={`w-full px-3 py-2 border rounded-xl text-sm focus:outline-none focus:border-blue-400 ${
                errors.amount ? "border-red-400" : "border-gray-300"
              }`}
            />
            <ValidationMessage error={errors.amount} />
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">
              Payment Date <span className="text-red-500">*</span>
            </label>
            <input
              type="date"
              name="payment_date"
              value={form.payment_date}
              onChange={handleChange}
              className={`w-full px-3 py-2 border rounded-xl text-sm focus:outline-none focus:border-blue-400 ${
                errors.payment_date ? "border-red-400" : "border-gray-300"
              }`}
            />
            <ValidationMessage error={errors.payment_date} />
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">Payment Mode</label>
            <SelectField variant="form"
              name="payment_mode"
              value={form.payment_mode}
              onChange={handleChange}
              className={`w-full px-3 py-2 border rounded-xl text-sm focus:outline-none focus:border-blue-400 ${errors.payment_mode ? 'border-red-400' : 'border-gray-300'}`}
            >
              {PAYMENT_MODES.map((m) => <option key={m} value={m}>{m}</option>)}
            </SelectField>
            <ValidationMessage error={errors.payment_mode} />
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">Reference / Cheque No.</label>
            <input
              type="text"
              name="reference_number"
              value={form.reference_number}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-xl text-sm focus:outline-none focus:border-blue-400"
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">Remarks</label>
            <textarea
              name="remarks"
              value={form.remarks}
              onChange={handleChange}
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded-xl text-sm focus:outline-none focus:border-blue-400 resize-none"
            />
          </div>

          {/* Receipt */}
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">
              Receipt {payment?.has_receipt ? "(replace existing)" : "(optional)"}
            </label>
            {payment?.has_receipt && !receipt && (
              <a href={payment.receipt_url} target="_blank" rel="noreferrer"
                className="inline-block text-xs text-orange-500 underline mb-1">
                View current receipt
              </a>
            )}
            <MediaCaptureField mode="document" onPick={(file) => setReceipt(file || null)} />
            {receipt && <p className="text-xs text-green-600 mt-1">{receipt.name}</p>}
          </div>
        </div>

        {/* Footer */}
        <div className="px-5 py-4 border-t border-gray-100 flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSubmit} disabled={saving} loading={saving}>
            {saving ? "Saving..." : "Update Payment"}
          </Button>
        </div>
      </div>
    </div>
  )
}

export default EditPaymentModal